import React, { useState, useEffect, useCallback } from 'react';
import { serviceConfigsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Pencil, Trash2, Link2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const ServiceConfigManager = () => {
  const [configs, setConfigs] = useState([]);
  const [allSubtaskTitles, setAllSubtaskTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [deletingConfig, setDeletingConfig] = useState(null);
  const [formName, setFormName] = useState('');
  const [formSubtasks, setFormSubtasks] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [subtaskSearch, setSubtaskSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [configsRes, titlesRes] = await Promise.all([
        serviceConfigsAPI.list(),
        serviceConfigsAPI.subtaskTitles(),
      ]);
      setConfigs(configsRes.data);
      setAllSubtaskTitles(titlesRes.data);
    } catch (err) {
      logger.error(err);
      toast.error('Failed to load service configurations');
    }
    setLoading(false);
  }, []); // serviceConfigsAPI, logger, toast are stable module-level refs

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = () => {
    setEditingConfig(null);
    setFormName('');
    setFormSubtasks([]);
    setSubtaskSearch('');
    setDialogOpen(true);
  };

  const openEdit = (config) => {
    setEditingConfig(config);
    setFormName(config.name);
    setFormSubtasks([...config.subtask_titles]);
    setSubtaskSearch('');
    setDialogOpen(true);
  };

  const openDelete = (config) => {
    setDeletingConfig(config);
    setDeleteDialogOpen(true);
  };

  const toggleSubtask = (title) => {
    setFormSubtasks(prev =>
      prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]
    );
  };

  const handleSubmit = async () => {
    if (!formName.trim()) {
      toast.error('Service name is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { name: formName.trim(), subtask_titles: formSubtasks };
      if (editingConfig) {
        await serviceConfigsAPI.update(editingConfig.id, payload);
        toast.success('Service configuration updated');
      } else {
        await serviceConfigsAPI.create(payload);
        toast.success('Service configuration created');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save configuration');
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!deletingConfig) return;
    setSubmitting(true);
    try {
      await serviceConfigsAPI.delete(deletingConfig.id);
      toast.success('Service configuration deleted');
      setDeleteDialogOpen(false);
      setDeletingConfig(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete configuration');
    }
    setSubmitting(false);
  };

  const filteredSubtaskTitles = allSubtaskTitles.filter(t =>
    !subtaskSearch || t.toLowerCase().includes(subtaskSearch.toLowerCase())
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-3">
            {[1, 2, 3].map(i => <div key={i} className="h-10 bg-muted rounded animate-pulse" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Link2 className="h-5 w-5" /> Service Configurations
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Configure which service tabs appear on client forms and which subtasks they auto-link to.
              </p>
            </div>
            <Button size="sm" onClick={openCreate} data-testid="add-service-config-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Service
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {configs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No service configurations found. Add one to get started.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service Name</TableHead>
                  <TableHead>Linked Subtasks</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map(cfg => (
                  <TableRow key={cfg.id} data-testid={`service-config-row-${cfg.id}`}>
                    <TableCell className="font-medium">{cfg.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {cfg.subtask_titles.length > 0 ? cfg.subtask_titles.map(t => (
                          <Badge key={t} variant="secondary" className="text-xs">{t}</Badge>
                        )) : (
                          <span className="text-xs text-muted-foreground italic">No subtasks linked</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(cfg)} data-testid={`edit-service-config-${cfg.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => openDelete(cfg)} data-testid={`delete-service-config-${cfg.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingConfig ? 'Edit Service Configuration' : 'Add Service Configuration'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Service Name *</Label>
              <Input
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="e.g. GST - Regular"
                data-testid="service-config-name-input"
              />
            </div>
            <div>
              <Label className="mb-2 block">Link Subtasks</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Select which subtasks clients should be auto-assigned to when this service is enabled.
              </p>
              <Input
                placeholder="Search subtasks..."
                value={subtaskSearch}
                onChange={e => setSubtaskSearch(e.target.value)}
                className="mb-2 h-8 text-sm"
                data-testid="subtask-search-input"
              />
              <ScrollArea className="h-48 border rounded-md p-2">
                {filteredSubtaskTitles.length > 0 ? filteredSubtaskTitles.map(title => (
                  <label
                    key={title}
                    className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-muted/50 cursor-pointer"
                    data-testid={`subtask-checkbox-${title}`}
                  >
                    <Checkbox
                      checked={formSubtasks.includes(title)}
                      onCheckedChange={() => toggleSubtask(title)}
                    />
                    <span className="text-sm">{title}</span>
                  </label>
                )) : (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    {allSubtaskTitles.length === 0
                      ? 'No subtasks exist in the system yet. Create tasks with subtasks first.'
                      : 'No subtasks match your search.'}
                  </p>
                )}
              </ScrollArea>
              {formSubtasks.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formSubtasks.map(t => (
                    <Badge key={t} variant="default" className="text-xs cursor-pointer" onClick={() => toggleSubtask(t)}>
                      {t} &times;
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="save-service-config-btn">
              {submitting ? 'Saving...' : (editingConfig ? 'Update' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Service Configuration</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete <strong>{deletingConfig?.name}</strong>? This won't remove existing client-subtask assignments.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={submitting} data-testid="confirm-delete-service-config-btn">
              {submitting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ServiceConfigManager;
